import type { FC } from 'react';
import type { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import type { PageData } from '@api/hooks/types';
import type { ContentType } from '@api/hooks/types';
import type { StaticPath } from '@api/mock/paths';
import { SWRConfig } from 'swr';
import { HeadBase } from '@components/Layout/components/Head/Head';
import { PageFlow } from '@components/Layout';
import { usePageData, getPageData, getFooterData, getHeaderData, getPaths } from '@api';
import { prepareApiUrl, trimSlash } from '@utils';
import { mocks } from '@api/mock';

export interface PageProps {
	fallback: PageData;
	endpoint?: string;
	needMock?: boolean;
}

export interface PageLayoutProps {
	endpoint?: string;
	needMock?: boolean;
}

const needMock = false;

const PageLayout: FC<PageLayoutProps> = ({ endpoint = null, needMock }) => {
	/*
	 * Получение данных через SWR (из контекста или запросом)
	 * Подробнее: https://swr.vercel.app/ru/docs/with-nextjs
	 */
	const { data } = usePageData(endpoint, needMock);

	return (
		<>
			<HeadBase {...data?.meta} />
			<PageFlow {...data} />
		</>
	);
};

const Page: NextPage<PageProps> = ({ fallback, endpoint, needMock }) => {
	return (
		/*
		 * Проброс в контекст SWR
		 * Подробнее: https://swr.vercel.app/ru/docs/with-nextjs
		 */
		<SWRConfig value={{ fallback }}>
			<PageLayout endpoint={endpoint} needMock={needMock} />
		</SWRConfig>
	);
};

export const getStaticPaths: GetStaticPaths = async () => {
	try {
		/*
		 * Получение статических путей страниц
		 */
		const paths = await getPaths('/paths');
		const pathsMap = paths.map((item: StaticPath) => {
			const routes = trimSlash(item.pathname).split('/');
			const locale = item.locale || (item.pathname.match(/^\/en/) ? 'en' : 'ru');
			return {
				params: { slug: routes },
				locale,
			};
		});

		/*
		 * Параметр fallback: blocking - для обработки случаев, когда роута нет в списке статических путей
		 * Например, в списке путей есть /events (страница сгенерируется как SSG), но нет /events/some-post-id (страница сгенерируется первый раз как SSR, затем некст будет отдавать ее как SSG)
		 * Подробнее: https://nextjs.org/docs/api-reference/data-fetching/get-static-paths#fallback-blocking
		 */
		return { paths: pathsMap, fallback: 'blocking' };
	} catch (error) {
		return { paths: [], fallback: 'blocking' };
	}
};

export const getStaticProps: GetStaticProps = async ({ params, locale }) => {
	try {
		const slug = params?.slug;
		const pathname = '/' + (Array.isArray(slug) ? slug.join('/') : slug);
		const endpoint = prepareApiUrl(pathname, locale, '/pages');
		const header = prepareApiUrl('/menu/header', locale);
		const footer = prepareApiUrl('/menu/footer', locale);

		if (endpoint === '/404') {
			return { notFound: true };
		}

		/*
		 * Эндпоинты страниц на моках
		 */
		const mockKeys = Object.keys(mocks);
		const isMockPage = mockKeys.some((key) => key === endpoint);

		/*
		 * Получение страницы (данные, мета-описание), а также данные шапки и подвала
		 */
		const pageData = await getPageData(endpoint, needMock || isMockPage);
		const headerData = await getHeaderData(header, needMock);
		const footerData = await getFooterData(footer, needMock);

		/*
		 * Если данных нет, редирект на 404
		 */
		if (!pageData || !headerData || !footerData) {
			return { notFound: true, revalidate: 2 };
		}

		/*
		 * Получение данных поблочно, если есть hypermedia-ссылки
		 * Например, блок новостей на странице событий { 'events/news': [...] }
		 */
		const endpoints: Record<string, any> = {};

		if (pageData?.data) {
			(pageData.data as Array<ContentType>)
				.filter((item) => item.links)
				.forEach((item) => {
					if (item?.links?.self && item.content) {
						endpoints[item.links.self] = item.content.items || item.content || {};
					}
				});
		}

		return {
			props: {
				/*
				 * Проброс fallback-параметра для SWR по всем полученным данным
				 */
				fallback: {
					[header]: headerData || {},
					[footer]: footerData || {},
					[endpoint]: pageData,
					...endpoints,
				},
				/*
				 * Проброс эндпоинта страницы
				 */
				endpoint,
				/*
				 * Отдельно передаю meta, чтобы получить в LayoutDefault
				 */
				meta: pageData?.meta,
				/*
				 * Флаг с требованием мока
				 */
				needMock: needMock || isMockPage,
			},
			revalidate: 10,
		};
	} catch (error) {
		return { notFound: true, revalidate: 2 };
	}
};

export default Page;
